import { Alert, Flex, Heading, InlineList, Text, View } from "@instructure/ui";
import c3 from "c3";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "../../components/Assessment/APDashboard/Results/Displays/resultTable.css";
import { transformResultsToTableData } from "../../components/Assessment/APDashboard/Results/Displays/utils/transformResultsToTableData";
import { Selector } from "../../components/Selectors/Selector";
import SimpleTable from "../../sections/Cards/SimpleTable/SimpleTable";
import ChartColors from "../../utils/ChartColors";
import { sort } from "../../utils/utils";

const PublicFlexibleAssessmentChart = ({
  measure,
  filters,
  canvasView,
  jaView,
}) => {
  const { t } = useTranslation();
  const [courseTitle, setCourseTitle] = useState("");
  const [overallTitle, setOverallTitle] = useState("");

  const [measureSubjects, setMeasureSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(
    Object.keys(measure.results)[0]
  );

  const [noOverallData, setNoOverallData] = useState(false);

  const presentationMode = measure.presentation_mode || "bar_chart";

  const drawGraph = (id, jsonData) => {
    const config = {
      bindto: `#${id}`,
      data: {
        type: "bar",
        json: sort(jsonData[0], (item) => item.name),
        keys: { x: "name", value: sort(jsonData[1]) },
      },
      axis: {
        x: { type: "category" },
        y: {
          label: {
            text:
              filters.masteryResults === "false"
                ? t("dashboard.avgAssignmentGrade")
                : t("dashboard.percentAchievedMastery"),
            position: "outer-middle",
          },
          min: 5,
          max: 100,
          tick: {
            values: [0, 20, 40, 60, 80, 100],
            format: function (d) {
              return d + "%";
            },
          },
        },
      },
      padding: { bottom: 25, left: 65 },
      color: { pattern: ChartColors },
      legend: { show: false },
      size: { height: 240 },
      zoom: { enabled: true },
      tooltip: {
        order: function (data1, data2) {
          return sort([data1, data2], (d) => d.name);
        },
      },
    };
    c3.generate(config);
  };

  useEffect(() => {
    const resultSubjects = Object.keys(measure.results);
    if (resultSubjects.length > 0) {
      const subjectList = resultSubjects.map((subject) => ({
        id: subject,
        label: subject,
      }));
      setMeasureSubjects(subjectList);
    }
  }, [measure.results]);

  const courseData = useMemo(() => {
    const results = measure.results[selectedSubject].assessment[canvasView];
    return results;
  }, [selectedSubject, canvasView, measure.results]);

  useEffect(() => {
    if (presentationMode === "bar_chart") {
      drawGraph("course-data-" + measure.id, courseData);
    }
    setCourseTitle(
      filters.masteryResults === "true"
        ? t("assessment.masteryAvgCourse")
        : t("assessment.gradeAvgCourse")
    );
  }, [courseData, presentationMode]);

  const getIsloJsonData = (res, categories) => {
    const json_data = [];
    const all_sub_key_name = res["all_subjects_results"][1][0];
    const ac_sub_key_name = res["ac_subject_results"][1][0];
    categories.forEach((category) => {
      const cat_index_all =
        res["all_subjects_results"][0].indexOf(category) + 1;
      const cat_index_ac = res["ac_subject_results"][0].indexOf(category) + 1;
      const all_sub_result = res["all_subjects_results"][1][cat_index_all];
      const ac_sub_result = res["ac_subject_results"][1][cat_index_ac];
      const category_json_entry = {
        name: category,
        [all_sub_key_name]:
          all_sub_result && cat_index_all
            ? Number(all_sub_result.toFixed(2))
            : all_sub_result,
        [ac_sub_key_name]:
          ac_sub_result && cat_index_ac
            ? Number(ac_sub_result.toFixed(2))
            : ac_sub_result,
      };
      json_data.push(category_json_entry);
    });
    return json_data;
  };

  const getPsloJsonData = (res, categories) => {
    const json_data = [];
    const ac_sub_key_name = res["ac_subject_results"][1][0];
    categories.forEach((category) => {
      const cat_index_ac = res["ac_subject_results"][0].indexOf(category) + 1;
      const ac_sub_result = res["ac_subject_results"][1][cat_index_ac];
      const all_sub_json_entry = {
        name: category,
        [ac_sub_key_name]:
          ac_sub_result && cat_index_ac
            ? Number(ac_sub_result.toFixed(2))
            : ac_sub_result,
      };
      json_data.push(all_sub_json_entry);
    });
    return json_data;
  };

  const overallData = useMemo(() => {
    const results = measure.results[selectedSubject].dash[jaView].results;
    if (results.ac_subject_results[0].length > 0) {
      setNoOverallData(false);
      const categories = results.ac_subject_results[0];
      const jsonData =
        results.all_subjects_results === null
          ? getPsloJsonData(results, categories)
          : getIsloJsonData(results, categories);
      const data = [
        jsonData,
        Object.keys(jsonData[0]).filter((key) => key !== "name"),
      ];
      return data;
    } else {
      setNoOverallData(true);
    }
    return [[], []];
  }, [selectedSubject, jaView, measure.results]);

  useEffect(() => {
    if (presentationMode === "bar_chart") {
      drawGraph("overall-data-" + measure.id, overallData);
    }
    setOverallTitle(
      filters.masteryResults === "true"
        ? t("assessment.masteryAvgOverall")
        : t("assessment.gradeAvgOverall")
    );
  }, [overallData, presentationMode]);

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{t("results.flexibleCanvasAssessment")}</Heading>
      </Flex.Item>
      <Flex.Item size="300px" padding="0 0 0 large">
        {measureSubjects.length > 0 ? (
          <Selector
            id={"subject-select-" + measure.id}
            label={t("global.departments")}
            options={measureSubjects}
            selectHandler={(sub) => setSelectedSubject(sub)}
            selectedItemId={selectedSubject}
          />
        ) : (
          <Skeleton style={{ height: 64, borderRadius: 5 }} />
        )}
      </Flex.Item>
      {courseData[0].length === 0 ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("results.noResults")}</Alert>
        </Flex.Item>
      ) : (
        <>
          {presentationMode === "bar_chart" && (
            <>
              <Flex.Item size="100%" padding="small 0 0 large">
                <View textAlign="center">
                  <Heading level="h4">{courseTitle}</Heading>
                  <div
                    id={"course-data-" + measure.id}
                    style={{ margin: "10px 0 -20px 0", width: "100%" }}
                  />
                </View>
              </Flex.Item>
              <Flex.Item size="100%" padding="0 0 medium xx-large">
                <View as="div" textAlign="center">
                  <InlineList delimiter="pipe" isUnstyled>
                    {courseData[1].map((category, index) => (
                      <InlineList.Item key={index}>
                        <div
                          style={{
                            display: "inline-block",
                            height: 10,
                            width: 10,
                            marginRight: 8,
                            backgroundColor: ChartColors[index],
                          }}
                        />
                        <Text as="span" size="small">
                          {category}
                        </Text>
                      </InlineList.Item>
                    ))}
                  </InlineList>
                </View>
              </Flex.Item>
            </>
          )}
          {presentationMode === "table" && (
            <Flex.Item size="100%" padding="0 0 medium large">
              <div
                aria-live="polite"
                aria-atomic="true"
                className="result-table-class"
              >
                {(() => {
                  const tableProps = transformResultsToTableData(
                    courseData,
                    courseTitle
                  );
                  return (
                    <SimpleTable
                      data={tableProps.data}
                      caption={<Text weight="bold">{tableProps.caption}</Text>}
                      rowTitle={tableProps.rowTitle}
                    />
                  );
                })()}
              </div>
            </Flex.Item>
          )}
        </>
      )}
      {noOverallData ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("results.noResults")}</Alert>
        </Flex.Item>
      ) : (
        <>
          {presentationMode === "bar_chart" && (
            <>
              <Flex.Item size="100%" padding="small 0 0 large">
                <View textAlign="center">
                  <Heading level="h4">{overallTitle}</Heading>
                  <div
                    id={"overall-data-" + measure.id}
                    style={{ margin: "10px 0 -20px 0", width: "100%" }}
                  />
                </View>
              </Flex.Item>
              <Flex.Item size="100%" padding="0 0 medium xx-large">
                <View as="div" textAlign="center">
                  <InlineList delimiter="pipe" isUnstyled>
                    {overallData[1].map((category, index) => (
                      <InlineList.Item key={index}>
                        <div
                          style={{
                            display: "inline-block",
                            height: 10,
                            width: 10,
                            marginRight: 8,
                            backgroundColor: ChartColors[index],
                          }}
                        />
                        <Text as="span" size="small">
                          {category}
                        </Text>
                      </InlineList.Item>
                    ))}
                  </InlineList>
                </View>
              </Flex.Item>
            </>
          )}
          {presentationMode === "table" && (
            <Flex.Item size="100%" padding="0 0 medium large">
              <div
                aria-live="polite"
                aria-atomic="true"
                className="result-table-class"
              >
                {(() => {
                  const tableProps = transformResultsToTableData(
                    overallData,
                    overallTitle
                  );
                  return (
                    <SimpleTable
                      data={tableProps.data}
                      caption={<Text weight="bold">{tableProps.caption}</Text>}
                      rowTitle={tableProps.rowTitle}
                    />
                  );
                })()}
              </div>
            </Flex.Item>
          )}
        </>
      )}
    </Flex>
  );
};

export default PublicFlexibleAssessmentChart;
